import React, { Fragment, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useApi } from 'model/Api';
import { WorkResource } from 'model/WorkResource';
import { generalStyle } from 'helper/Style';
import Layout from 'component/part/Layout';
import BackLink from 'component/part/BackLink';
import FormContent from 'component/part/FormContent';
import OperationTrigger from 'component/part/OperationTrigger';
import Select from 'component/part/Select';
import H2 from 'component/part/H2';

type SortStatus = {
  type: WorkResource['type'];
  draggIndex: number | null;
  dropIndex: number | null;
};

const workResourceKeys: (keyof WorkResource)[] = ['name', 'unitName', 'valueType', 'unitPrice', 'group', 'order'];

type PageWorkResourceParams = {
  [key: string]: {
    types: WorkResource['type'][];
    typeLabel: string;
  };
};

const pageWorkResourceParams: PageWorkResourceParams = {
  machinery: { types: ['machinery'], typeLabel: '重機' },
  att: { types: ['att'], typeLabel: 'ATT' },
  car: { types: ['car'], typeLabel: '車両' },
  ['industrial-waste']: { types: ['industrial_waste'], typeLabel: '産廃' },
  general: { types: ['person', 'tool', 'car_related', 'other'], typeLabel: '工具' },
};

const pageWorkResourceLabels: { [type in WorkResource['name']]: string } = {
  machinery: '重機',
  att: 'ATT',
  car: '車両',
  industrial_waste: '産廃',
  person: '職種',
  tool: '工具',
  car_related: '車両関連',
  other: 'その他',
};

const defaultValueTypes: { [type in WorkResource['name']]: WorkResource['valueType'] } = {
  machinery: 'count',
  att: 'count',
  car: 'count',
  industrial_waste: 'volume',
  person: 'count',
  tool: 'count',
  car_related: 'text',
  other: 'text',
};

type PageWorkResources<TS extends WorkResource['type'][]> = {
  [type in TS[number]]: WorkResource[];
};

const makeInitialPageWorkResources = <TS extends WorkResource['type'][]>(types: TS): PageWorkResources<TS> =>
  types.reduce((list, type) => ({ ...list, [type]: [] }), {}) as PageWorkResources<TS>;

const valueTypes: { value: WorkResource['valueType'], label: string }[] = [
  { value: 'count', label: '数値選択（1〜10）' },
  { value: 'volume', label: '数値入力' },
  { value: 'text', label: 'テキスト入力' },
  { value: 'free_text', label: '自由記述' },
];

const style = {
  form: {
    ...generalStyle.form,
    gap: '0',
    width: '80rem',
  },
  formRow: (type: WorkResource['type'], valueType: WorkResource['valueType']) => ({
    ...generalStyle.formRow,
    display: 'grid',
    gridTemplateColumns: 'auto auto 1fr auto',
    alignItems: 'end',
    gap: '1rem',
    padding: '0.8rem 0 1.2rem',
    ['&:nth-of-type(1)']: {
      paddingTop: '0',
    },
    ['&:nth-of-last-type(1)']: {
      paddingBottom: '0',
    },
    ['& > div:nth-of-type(1)']: {
      width: '1.6rem',
      height: '3rem',
      backgroundColor: '#aaaacc',
      cursor: 'all-scroll',
    },
    ['& > div:nth-of-type(2)']: {
      display: 'grid',
      gridTemplateColumns: (() => {
        switch (type) {
          case 'person':
            return  '7fr 1fr 2fr';
          case 'machinery':
          case 'att':
          case 'car':
            return '8fr 2fr';
          default:
            return ['text', 'free_text'].includes(valueType)
              ? '8fr 2fr'
              : '5fr 2fr 1fr 2fr';
        }
      })(),
      gap: '2rem',
    },
  }),
  sortTarget: {
    paddingBottom: '0.8rem',
    borderBottom: 'solid 0.2rem #aaaacc',
  },
  addButton: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    width: 'fit-content',
    padding: '0 1rem',
    height: '3rem',
    backgroundColor: '#eeeeee',
    border: 'solid 0.1rem #aaaaaa',
    borderRadius: '0.3rem',
    cursor: 'pointer',
    ['&:hover']: {
      backgroundColor: '#cccccc',
    },
  },
};

const WorkResourceEdit: React.FC = () => {
  const api = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const urlParams = location.pathname.match(/^\/(.*?)\//) || [];
  const typeCode = urlParams[1] || 'general';
  const { types, typeLabel } = pageWorkResourceParams[typeCode] || pageWorkResourceParams.general;
  const [workResources, setWorkResources] = useState<PageWorkResources<typeof types>>(makeInitialPageWorkResources(types));
  const [originalWorkResources, setOriginalWorkResources] = useState<WorkResource[]>([]);
  const [deletedWorkResourceIds, setDeletedWorkResourceIds] = useState<number[]>([]);
  const [sortStatus, setSortStatus] = useState<SortStatus>({ type: types[0], draggIndex: null, dropIndex: null });

  const onAddWorkResource = (type: (typeof types)[number], index: number) => {
    const prevItem = workResources[type][index - 1] || workResources[type][index];
    const blankItem: WorkResource = {
      id: 0,
      clientId: 1,
      type,
      name: '',
      valueType: prevItem?.valueType || defaultValueTypes[type],
      group: prevItem?.group || 1,
      order: 1,
    };
    workResources[type].splice(index, 0, blankItem);
    setWorkResources({ ...workResources });
  };

  const onChangeWorkResource = <T extends keyof WorkResource>(
    workResource: WorkResource,
    key: T,
    value: WorkResource[T]
  ) => {
    if (workResources) {
      workResource[key] = value;
      setWorkResources({ ...workResources });
    }
  };

  const onDeleteWorkResource = (type: (typeof types)[number], index: number) => {
    const { id } = workResources[type][index];
    workResources[type].splice(index, 1);
    setWorkResources({ ...workResources });
    setDeletedWorkResourceIds([...deletedWorkResourceIds, id]);
  };

  const onStartDragg = (type: (typeof types)[number], index: number) => {
    setSortStatus({ ...sortStatus, type, draggIndex: index });
  };

  const onOverDragg = (
    type: (typeof types)[number],
    index: number,
    event: React.DragEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
    if (type === sortStatus.type) {
      setSortStatus({ ...sortStatus, dropIndex: index });
    }
  };

  const onEndDragg = (
    type: (typeof types)[number],
    index: number | null,
    event: React.DragEvent<HTMLDivElement>
  ) => {
    event.preventDefault();
    if (
      type === sortStatus.type &&
      (sortStatus.draggIndex ?? index) !== null &&
      sortStatus.dropIndex !== null
    ) {
      const draggIndex = (sortStatus.draggIndex ?? index) as number;
      const dropIndex = sortStatus.dropIndex + (draggIndex > sortStatus.dropIndex ? 1 : 0);
      const [workResource] = workResources[type].splice(draggIndex, 1);
      workResources[type].splice(dropIndex, 0, workResource);
      setWorkResources({ ...workResources });
    }
    setSortStatus({ ...sortStatus, draggIndex: null, dropIndex: null });
  };

  const onSubmit = async () => {
    const [createdWorkResources, updatedWorkResources]: WorkResource[][] = [[], []];
    types.forEach((type) => {
      workResources[type].forEach((item, index) => {
        item.order = index + 1;
        if (item.id) {
          const originalItem = originalWorkResources.find(({ id }) => id === item.id);
          const isUpdated = workResourceKeys.reduce(
            (judge, key: keyof WorkResource) => judge || (originalItem?.[key] !== item[key]),
            false,
          );
          if (isUpdated) {
            updatedWorkResources.push(item);
          }
        } else {
          createdWorkResources.push(item);
        }
      });
    });

    return !!(await api.workResource.updates({
      createList: createdWorkResources,
      updateList: updatedWorkResources,
      deleteList: deletedWorkResourceIds,
    }));
  };

  useEffect(() => {
    const initialWorkResources = makeInitialPageWorkResources(types);
    setWorkResources(initialWorkResources);
    (async () => {
      const workResourcesResponse = await api.workResource.getsByOwn({ types });
      setOriginalWorkResources(workResourcesResponse);
      workResourcesResponse.forEach((item) => {
        initialWorkResources[item.type].push({ ...item });
      });
      setWorkResources(initialWorkResources);
    })();
  }, [typeCode]);

  return <Layout>
    <BackLink link={`/${typeCode}/list`} label={`${typeLabel}一覧`}></BackLink>
    <div css={generalStyle.form}>
      {types.map((type) => (
        <Fragment key={type}>
          <div css={style.form}>
            <H2>{pageWorkResourceLabels[type]}編集</H2>
            {(workResources[type] || []).map((workResource, typeIndex) => (
              <div
                draggable={true}
                onDragStart={() => onStartDragg(type, typeIndex)}
                onDragOver={(event) => onOverDragg(type, typeIndex, event)}
                onDragEnd={(event) => onEndDragg(type, typeIndex, event)}
                onDrop={(event) => onEndDragg(type, null, event)}
                css={[
                  style.formRow(type, workResource.valueType),
                  ...((type === sortStatus.type && typeIndex === sortStatus.dropIndex) ? [style.sortTarget] : [])
                ]}
                key={`${type}.${typeIndex}`}
              >
                <div />
                <button onClick={() => onDeleteWorkResource(type, typeIndex)} css={style.addButton}>ー</button>
                <div>
                  <FormContent label='名称'>
                    <input
                      type='text'
                      value={workResource.name}
                      onChange={(event) => onChangeWorkResource(workResource, 'name', event.target.value)}
                    />
                  </FormContent>
                  {(type !== 'person' && !['text', 'free_text'].includes(workResource.valueType)) && (
                    <FormContent label='単価'>
                      <div css={generalStyle.inputArea}>
                        <input
                          type='number'
                          value={workResource.unitPrice}
                          onChange={(event) => onChangeWorkResource(workResource, 'unitPrice', parseInt(event.target.value))}
                        />
                        <span>円</span>
                      </div>
                    </FormContent>
                  )}
                  {(!['machinery', 'att', 'car'].includes(type) && !['text', 'free_text'].includes(workResource.valueType)) && (
                    <FormContent label='単位'>
                      <input
                        type='text'
                        value={workResource.unitName}
                        onChange={(event) => onChangeWorkResource(workResource, 'unitName', event.target.value)}
                      />
                    </FormContent>
                  )}
                  {!['machinery', 'att', 'car'].includes(type) && (
                    <FormContent label='入力タイプ'>
                      <Select
                        list={valueTypes}
                        value={valueTypes.find(({ value }) => value === workResource.valueType)}
                        labelKey='label'
                        onChange={(item) => item && onChangeWorkResource(workResource, 'valueType', item.value)}
                      />
                    </FormContent>
                  )}
                </div>
                <button onClick={() => onAddWorkResource(type, typeIndex)} css={style.addButton}>
                  <img src={'/image/icon/arrow-up-left.svg'} />上に追加
                </button>
              </div>
            ))}
          </div>
          <div css={generalStyle.formRow}>
            <button onClick={() => onAddWorkResource(type, workResources[type].length)} css={style.addButton}>＋{pageWorkResourceLabels[type]}追加</button>
          </div>
        </Fragment>
      ))}
      <OperationTrigger
        onSubmit={onSubmit}
        onOk={() => navigate(`/${typeCode}/list`)}
        buttonText='更新'
        confirmText='変更内容を確定します'
        submitText='更新'
        doneText='更新が完了しました'
        errorText='更新に失敗しました'
      />
    </div>
  </Layout>;
};

export default WorkResourceEdit;
